/* eslint-disable camelcase */
import React from 'react';
import {
  Typography,
  CircularProgress,
  Link,
} from '@mui/material';
import { Formik, Form } from 'formik';
import { useHistory } from 'react-router-dom';
import type { PLRFormProps } from '../../../../../../types/preloan';
import { FormSchemaV3 } from '../../../../../../types/preloan/v3';
import Header from './components/Header';
import ResultLabels from './components/ResultsLabels';
import NoFilterResults from './components/NoFilterResults';
import { getContactMessage, getRedirectUrl } from '../07_lastStep/components/messageSwitches';
import { getOriginButtonLabel } from '../../formLogic';
import analytics from '../../../../../../utils/analytics';
import ComplementOffer from '../../../ComplementInvite/ComplementOffer';
import ComplementLeadResultDialog from '../../../ComplementInvite/ComplementLeadResultDialog';
import { inviteePlrCategory, LEAD_OFFER_PAGE, leadResultCategory } from '../../../ComplementInvite/analyticsPages';

export default function RejectedForm<T extends FormSchemaV3>(props: PLRFormProps<T>) {
  const {
    formRef,
    formSchema,
    handleEnableLoading,
  } = props;
  const history = useHistory();

  React.useEffect(() => {
    handleEnableLoading(false);
    const showComplementOffer = formSchema.complementOfferAvailable
      && typeof formSchema.leadComplementAccepted !== 'boolean';
    const leadCategory = leadResultCategory(formSchema.isRedirect);
    if (formSchema.results) {
      const rejectedCategory = inviteePlrCategory(
        formSchema.complementToken,
        formSchema.isRedirect ? `PLR - ${formSchema.isRedirect}` : 'PLR - RESULTADO',
      );
      formSchema.results.forEach((result) => {
        analytics.page(
          rejectedCategory,
          'USUARIO RECHAZADO',
          {
            trigger: 'backend',
            productType: result.productType,
            risk_models: result.hardFilterResults.risk_models,
            lagunes: result.hardFilterResults.lagunes,
            max_age: result.hardFilterResults.max_age,
            min_age: result.hardFilterResults.min_age,
            punished_debt: result.hardFilterResults.punished_debt,
            alimony_debtor: result.hardFilterResults.alimony_debtor,
            min_emp_length: result.hardFilterResults.min_emp_length,
            min_net_salary: result.hardFilterResults.min_net_salary,
            min_brute_salary: result.hardFilterResults.min_brute_salary,
            leverage_total_debt: result.hardFilterResults.leverage_total_debt,
            max_financial_stress: result.hardFilterResults.max_financial_stress,
            product_availability: result.hardFilterResults.product_availability,
            leverage_consumer_debt: result.hardFilterResults.leverage_consumer_debt,
            min_loan_policy_amount: result.hardFilterResults.min_loan_policy_amount,
            max_dpp_financial_stress: result.hardFilterResults.max_dpp_financial_stress,
            max_mortgage_financial_stress: result.hardFilterResults.max_mortgage_financial_stress,
            protest_and_delinquency_1year: result.hardFilterResults.protest_and_delinquency_1year,
            protest_and_delinquency_5years: result.hardFilterResults.protest_and_delinquency_5years,
            external_score: result.hardFilterResults.external_score,
            pre_required_associated_product_approved: result.hardFilterResults
              .pre_required_associated_product_approved,
          },
        );
      });
    }
    if ((!formSchema.results || formSchema.results?.length === 0) && formSchema.adminComment) {
      const simulatorAvailability = !formSchema.adminComment.includes('capacidades de pago');
      const workerTypeAllowed = !formSchema.adminComment.includes('contrato de trabajo')
      && !formSchema.adminComment.includes('deshabilitada la evaluación');
      const rejectedCategory = inviteePlrCategory(
        formSchema.complementToken,
        formSchema.isRedirect ? `PLR - ${formSchema.isRedirect}` : 'PLR - RESULTADO',
      );
      analytics.page(
        rejectedCategory,
        'USUARIO RECHAZADO',
        {
          trigger: 'frontend',
          simulator_availability: simulatorAvailability,
          workerType_allowed: workerTypeAllowed,
          admin_rejection: !simulatorAvailability || !workerTypeAllowed,
        },
      );
    }
    if (showComplementOffer) {
      analytics.page(leadCategory, LEAD_OFFER_PAGE, { screen: 'rejected' });
    }
    if (formSchema.origin === 'portal-usuario') {
      setTimeout(() => {
        history.push('/account');
      }, 4000);
      return;
    }
    if (formSchema.config.preLoanRequestConfig?.rejectedStepText?.autoRedirect) {
      setTimeout(() => {
        window.location.href = getRedirectUrl(
          formSchema.config,
          true,
        );
      }, 5000);
    }
  }, []);

  const availableRedirectToDpp = formSchema.partner === 'Banca.Me' && !formSchema.origin.includes('dpp');
  const getRedirectToDppUrl = () => {
    if (formSchema.origin === 'referral' && formSchema.referralCode) {
      return `https://${process.env.REACT_APP_BUCKET_NAME}/preloan?origin=bancame-dpp-referral&partner=Banca.Me&referralCode=${formSchema.referralCode}`;
    }
    return `https://${process.env.REACT_APP_BUCKET_NAME}/preloan?origin=bancame-dpp-rejected-microloan&partner=Banca.Me`;
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={formSchema}
      onSubmit={(values) => {
        analytics.track('Click Boton', {
          initiator: 'usuario',
          text: getOriginButtonLabel(formSchema.partner),
        });
        window.location.href = getRedirectUrl(values.config, false);
      }}
    >
      {({
        values,
      }) => (
        <Form>
          <Header
            formSchema={values}
          />
          {formSchema.config.preLoanRequestConfig?.rejectedStepText?.showResults
            ? (
              <div style={{
                overflowY: 'auto',
                maxHeight: '175px',
                margin: 'auto',
                backgroundColor: values.config.styles?.backgroundSecondaryColor,
                marginTop: '10px',
                borderRadius: '10px',
              }}
              >
                {values.results && values.results.length > 0
            && (
              <ResultLabels
                results={values.results}
                origin={{
                  originLabel: values.origin,
                  partner: values.partner,
                  partnerProducts: values.products,
                }}
              />
            )}
                {(!values.results || values.results?.length === 0)
            && (
              <NoFilterResults
                adminComment={values.adminComment}
              />
            )}
              </div>
            ) : (
              <Typography component="div" variant="body2" align="center" style={{ marginTop: 20 }}>
                Si tienes alguna duda o pregunta,
                {getContactMessage(formSchema.config)}
              </Typography>
            )}
          {formSchema.complementOfferAvailable
            && typeof formSchema.leadComplementAccepted !== 'boolean' && (
            <ComplementOffer
              partner={{
                partnerName: formSchema.partner,
                config: formSchema.config,
                products: formSchema.products,
              }}
              token={formSchema.PLRtoken}
              pageCategory={leadResultCategory(formSchema.isRedirect)}
              leadApproved={false}
            />
          )}
          <ComplementLeadResultDialog
            leadComplementAccepted={formSchema.leadComplementAccepted}
            ownPreApproved={false}
            leadFirstName={formSchema.complementLeadFirstName}
          />
          {formSchema.config.preLoanRequestConfig?.rejectedStepText?.autoRedirect && (
            <Typography component="div" variant="body2" align="center" style={{ marginTop: 20 }}>
              Se te redirigirá en breve...
              {' '}
              <CircularProgress size={20} />
            </Typography>
          )}
          {availableRedirectToDpp && (
            <Typography component="div" variant="caption" align="center" style={{ marginTop: 25 }}>
              <Link
                href={getRedirectToDppUrl()}
                target="_blank"
                rel="noopener noreferrer"
              >
                Estoy en una compañia afiliada y quiero evaluarme para Descuento por Planilla.
              </Link>
            </Typography>
          )}
        </Form>
      )}
    </Formik>
  );
}

RejectedForm.defaultProps = {
  debts: undefined,
};
